import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import ForceGraph2D from 'react-force-graph-2d';
import { Compass, ZoomIn, ZoomOut, RotateCcw, Info } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function ExplorerPage() {
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);
  const graphRef = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });

  useEffect(() => {
    const fetchGraphData = async () => {
      try {
        const res = await axios.get(`${API}/graph/data`);
        setGraphData(res.data);
      } catch (error) {
        console.error('Error fetching graph data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGraphData();
  }, []);

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.offsetWidth,
          height: window.innerHeight - 200
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const handleNodeClick = useCallback((node) => {
    setSelectedNode(node);
    // Center on node
    if (graphRef.current) {
      graphRef.current.centerAt(node.x, node.y, 500);
      graphRef.current.zoom(2, 500);
    }
  }, []);

  const handleZoomIn = () => {
    if (graphRef.current) {
      const zoom = graphRef.current.zoom();
      graphRef.current.zoom(zoom * 1.5, 300);
    }
  };

  const handleZoomOut = () => {
    if (graphRef.current) {
      const zoom = graphRef.current.zoom();
      graphRef.current.zoom(zoom / 1.5, 300);
    }
  };

  const handleReset = () => {
    if (graphRef.current) {
      graphRef.current.zoomToFit(400);
    }
    setSelectedNode(null);
  };

  const nodeColor = (node) => {
    switch (node.type) {
      case 'person': return '#00F0FF';
      case 'phenomenon': return '#FFD700';
      case 'concept': return '#B388FF';
      case 'event': return '#FF6B6B';
      default: return '#FFFFFF';
    }
  };

  return (
    <div className="min-h-screen py-8 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Compass className="w-8 h-8 text-cyan-400" />
              <h1 className="font-heading text-3xl md:text-4xl font-bold" data-testid="explorer-title">
                Phenomena Explorer
              </h1>
            </div>
            <p className="text-white/50">
              Interactive connection graph. Click nodes to explore relationships.
            </p>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleZoomIn} className="text-white/60 hover:text-white" data-testid="zoom-in-btn">
              <ZoomIn className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleZoomOut} className="text-white/60 hover:text-white" data-testid="zoom-out-btn">
              <ZoomOut className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleReset} className="text-white/60 hover:text-white" data-testid="reset-btn">
              <RotateCcw className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-6 text-sm">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-cyan-400" />
            <span className="text-white/60">Person</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-yellow-400" />
            <span className="text-white/60">Phenomenon</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-purple-400" />
            <span className="text-white/60">Concept</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-red-400" />
            <span className="text-white/60">Event</span>
          </div>
        </div>

        {/* Graph Container */}
        <div ref={containerRef} className="card-dark overflow-hidden graph-container" data-testid="graph-container">
          {loading ? (
            <div className="flex items-center justify-center" style={{ height: dimensions.height }}>
              <div className="text-center">
                <div className="w-12 h-12 border-2 border-white/20 border-t-cyan-400 rounded-full animate-spin mx-auto mb-4" />
                <p className="text-white/50 font-mono text-sm">Loading connections...</p>
              </div>
            </div>
          ) : (
            <ForceGraph2D
              ref={graphRef}
              graphData={graphData}
              width={dimensions.width}
              height={dimensions.height}
              nodeColor={nodeColor}
              nodeLabel={(node) => `${node.name} (${node.type})`}
              nodeRelSize={6}
              linkColor={() => 'rgba(255,255,255,0.1)'}
              linkWidth={1}
              linkDirectionalParticles={2}
              linkDirectionalParticleWidth={2}
              linkDirectionalParticleColor={() => 'rgba(0,240,255,0.5)'}
              onNodeClick={handleNodeClick}
              backgroundColor="transparent"
              nodeCanvasObject={(node, ctx, globalScale) => {
                const label = node.name;
                const fontSize = 12 / globalScale;
                ctx.font = `${fontSize}px Inter`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                
                // Draw node
                ctx.beginPath();
                ctx.arc(node.x, node.y, 6, 0, 2 * Math.PI);
                ctx.fillStyle = nodeColor(node);
                ctx.fill();
                
                // Draw label
                if (globalScale > 0.5) {
                  ctx.fillStyle = 'rgba(255,255,255,0.8)';
                  ctx.fillText(label, node.x, node.y + 12);
                }
              }}
            />
          )}
        </div>

        {/* Selected Node Info */}
        {selectedNode && (
          <div className="card-dark p-6 mt-6 animate-fade-in-up" data-testid="selected-node-info">
            <div className="flex items-start gap-4">
              <Info className="w-5 h-5 text-cyan-400 mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-heading text-xl font-semibold mb-1">{selectedNode.name}</h3>
                <p className="text-cyan-400 text-sm font-mono uppercase mb-3">{selectedNode.type}</p>
                <p className="text-white/60 text-sm">
                  Click on other nodes to explore connections, or use the controls above to navigate the graph.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
